import type {Participant, ParticipantsResponse} from '@github-ui/dotcom-schema/types/pull_requests/v1/participants'
import {useParticipantsQuery} from '../data/route-queries'
import styles from './Participants.module.css'
import {UserSectionSkeleton} from '../UserSectionSkeleton'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {SidebarSection} from '../SidebarSection'
import {Link} from '@github-ui/react-router'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {isCopilot} from '@github-ui/assignees/copilot-user'

export function Participants() {
  const {data: participantsData, isPending, isError} = useParticipantsQuery()

  if (isPending && !participantsData) {
    return (
      <SidebarSection title="Participants">
        <UserSectionSkeleton placeholderRows={1} />
      </SidebarSection>
    )
  }

  if (isError && !participantsData) {
    return (
      <SidebarSection title="Participants">
        <span className={styles.emptyState}>Unable to load participants</span>
      </SidebarSection>
    )
  }

  return (
    <SidebarSection
      title={`${participantsData.totalCount} ${participantsData.totalCount === 1 ? 'participant' : 'participants'}`}
    >
      <ParticipantsComponent participantsData={participantsData} />
    </SidebarSection>
  )
}

function ParticipantsComponent({participantsData}: {participantsData: ParticipantsResponse}) {
  const participants = participantsData.participants ?? []

  if (participants.length === 0) {
    return <span className={styles.emptyState}>No participants</span>
  }

  const hiddenParticipantsCount = participantsData.totalCount - participants.length

  return (
    <div className={styles.participants}>
      {participants.map(participant => (
        <ParticipantAvatar key={participant.id.toString()} participant={participant} />
      ))}
      {hiddenParticipantsCount > 0 && <span className={styles.hiddenCount}>+{hiddenParticipantsCount} more</span>}
    </div>
  )
}

function ParticipantAvatar({participant}: {participant: Participant}) {
  const getHovercardAttributes = useGetHovercardAttributesForType()
  const isCopilotUser = isCopilot(participant.displayLogin)
  const hovercardAttributes = isCopilotUser
    ? getHovercardAttributes('copilot', {botSlug: participant.slug || participant.displayLogin})
    : getHovercardAttributes('user', {login: participant.displayLogin})

  return (
    <Link aria-label={participant.displayLogin} to={participant.profilePath} {...hovercardAttributes}>
      <GitHubAvatar src={participant.avatarUrl} size={24} />
    </Link>
  )
}
